import {useCallback, useState} from 'react';
import {Edge, Node, useStoreApi} from 'reactflow';

import {GetHandlesFunc} from 'ui-library/reactflow/handles/useGetNodeHandlesData';

const MAX_WAIT_MS = 5000;

interface UseWaitForLayoutReadyParams {
  nodes: Array<Node>;
  edges: Array<Edge>;
  getHandles: GetHandlesFunc;
}

interface UseWaitForLayoutReadyResult {
  isLayoutInProgress: boolean;
  runWhenLayoutReady: <T>(layoutFn: () => Promise<T>) => Promise<T>;
}

/**
 * Manages viewport culling lifecycle for layout operations. Polls until every node has been
 * measured and every handle referenced by an edge is registered in the ReactFlow store,
 * then executes the provided layout function.
 *
 * This is necessary because the ELK layout algorithm requires accurate node dimensions
 * and handle positions — which are unavailable for nodes that were off-screen and
 * therefore never mounted by ReactFlow's virtualization (`onlyRenderVisibleElements`).
 */
export function useWaitForLayoutReady({
  nodes,
  edges,
  getHandles,
}: UseWaitForLayoutReadyParams): UseWaitForLayoutReadyResult {
  const reactFlowStoreApi = useStoreApi();
  const [isLayoutInProgress, setIsLayoutInProgress] = useState(false);

  const waitForReady = useCallback(
    (): Promise<void> =>
      new Promise((resolve) => {
        const startTime = Date.now();

        const checkReady = (): void => {
          if (Date.now() - startTime > MAX_WAIT_MS) {
            resolve();
            return;
          }

          const {nodeInternals} = reactFlowStoreApi.getState();
          const allDimensioned = nodes.every((node) => {
            const internal = nodeInternals.get(node.id);
            return (
              internal?.width != null && internal.width > 0 && internal?.height != null && internal.height > 0
            );
          });
          if (!allDimensioned) {
            requestAnimationFrame(checkReady);
            return;
          }

          const allHandleIds = new Set<string>();
          for (const node of nodes) {
            for (const handle of getHandles(node.id)) {
              if (handle.id) {
                allHandleIds.add(handle.id);
              }
            }
          }
          const hasMissingHandles = edges.some(
            (edge) =>
              (edge.sourceHandle != null && !allHandleIds.has(edge.sourceHandle)) ||
              (edge.targetHandle != null && !allHandleIds.has(edge.targetHandle))
          );

          if (hasMissingHandles) {
            requestAnimationFrame(checkReady);
          } else {
            resolve();
          }
        };
        requestAnimationFrame(checkReady);
      }),
    [nodes, edges, reactFlowStoreApi, getHandles]
  );

  const runWhenLayoutReady = useCallback(
    async <T>(layoutFn: () => Promise<T>): Promise<T> => {
      setIsLayoutInProgress(true);
      try {
        await waitForReady();
        return await layoutFn();
      } finally {
        setIsLayoutInProgress(false);
      }
    },
    [waitForReady]
  );

  return {isLayoutInProgress, runWhenLayoutReady};
}
